import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { Leaving } from '@volee/auth'
import { toast } from '@volee/ui'
import { useCallback } from 'react'

import { ME_KEY } from '../auth/me.ts'
import { accountInUse, holdsSeveralAccounts } from './device-accounts.ts'
import { enrolmentLinkMemory } from './enrolment-link-memory.ts'
import { messages } from '../messages/index.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { auth } from '../services/auth.ts'

// The way out, in one place because four surfaces offer it.

/** Revokes everywhere, as «Esci» always did, while the phone holds one account of its own; among
 *  several, or for a child's, this phone only (`RevokeToken`): their own phone stays signed in. */
function leaveTheAccountInUse(): Promise<Leaving> {
  const accounts = auth.accounts.get()
  const inUse = accountInUse(accounts)
  if (inUse !== undefined && (holdsSeveralAccounts(accounts) || inUse.kind !== 'own')) {
    return auth.accounts.leave(inUse.sub)
  }
  return auth.password.leave({ navigate: false })
}

function leaveEveryAccount(): Promise<Leaving> {
  return auth.accounts.leaveAll()
}

/** The session off this phone first, then the door, and Cognito's answer last (#255): the page
 *  left is never drawn empty, and nobody waits on a revocation to see the door. */
function useLeaving(leave: () => Promise<Leaving>): () => Promise<void> {
  const cache = useQueryClient()
  const navigate = useNavigate()

  return useCallback(async () => {
    const { revocation } = await leftThisPhone(leave)
    // Nobody, not an empty cache: the page left keeps what it shows, and the door asks nothing.
    cache.setQueryData(ME_KEY, null)
    enrolmentLinkMemory.forget()
    // The accounts still here are offered by the sign-in; the literal paths, not the route objects:
    // those modules import the shell that renders this.
    const left = auth.accounts.get().list.length > 0
    await navigate(left ? { to: NAV_PATHS.signIn, search: {} } : { to: NAV_PATHS.home, search: {} })
    // At the door and not after Cognito: whoever signs in next must not read what this one read.
    forgetAllButMe(cache)
    // Another device, or this token, may still be signed in, and the user is the only one who can
    // act on it; not said to somebody who has signed in since.
    if ((await revocation) === 'failed' && auth.accounts.get().active === null) {
      toast.error(messages.navigation.signOutPartial)
    }
  }, [cache, navigate, leave])
}

type Revocation = Promise<'revoked' | 'failed'>

/** How the revocation went, read from the start so a failure offline is never an unhandled
 *  rejection; a way out that failed here is reported as one that failed there. In an object: an
 *  `async` function returning the bare promise would wait for it. */
async function leftThisPhone(
  leave: () => Promise<Leaving>,
): Promise<{ readonly revocation: Revocation }> {
  try {
    const { revoked } = await leave()
    return {
      revocation: revoked.then(
        () => 'revoked' as const,
        () => 'failed' as const,
      ),
    }
  } catch {
    return { revocation: Promise.resolve('failed' as const) }
  }
}

/** Everything the identity read, and its writes in flight; `['me']` stays nobody. */
function forgetAllButMe(cache: QueryClient) {
  cache.removeQueries({ predicate: (query) => query.queryKey[0] !== ME_KEY[0] })
  cache.getMutationCache().clear()
}

export function useSignOut(): () => Promise<void> {
  return useLeaving(leaveTheAccountInUse)
}

/** Every account off this phone, each revoked here only. */
export function useSignOutOfEveryAccount(): () => Promise<void> {
  return useLeaving(leaveEveryAccount)
}
